import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { 
  Library, 
  Plus, 
  Trash2, 
  ExternalLink, 
  Search,
  BookOpen,
  FileText,
  UploadCloud,
  X
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";

export default function InstituteBooks() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  const [formData, setFormData] = useState({
    title: "",
    author: "",
    category: "General",
    description: "",
    thumbnail: "",
    pdf_url: ""
  });
  
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchBooks();
  }, []);

  const fetchBooks = async () => {
    try {
      const res = await api.get("/books");
      setBooks(res.data);
    } catch (err) {
      toast.error("Failed to load books");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload/file", formData);
      const file_url = res.data.url;

      setFormData(prev => ({ ...prev, [field]: file_url }));
      toast.success(`${field === 'pdf_url' ? 'PDF' : 'Thumbnail'} uploaded!`);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/books", formData);
      toast.success("Book added successfully!");
      setShowModal(false);
      setFormData({ title: "", author: "", category: "General", description: "", thumbnail: "", pdf_url: "" });
      fetchBooks();
    } catch (err) {
      toast.error("Failed to add book");
    }
  };

  

  const filteredBooks = books.filter(b => 
    b.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 lg:p-10 space-y-10 font-['Inter',sans-serif]">
      <Toaster position="top-center" />
      
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 flex items-center gap-3">
            <Library className="text-indigo-600" /> Digital Library
          </h1>
          <p className="text-slate-500 font-medium mt-1">Manage recommended reading materials for students</p>
        </div>
        
        <button 
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-black rounded-2xl shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all"
        >
          <Plus size={20} /> Add New Book
        </button>
      </div>

      {/* 🔍 SEARCH & FILTERS */}
      <div className="relative max-w-md">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
        <input 
          type="text"
          placeholder="Search by title or category..."
          className="w-full pl-12 pr-4 py-4 bg-white border border-slate-100 rounded-2xl shadow-sm outline-none focus:ring-2 focus:ring-indigo-600/10 transition-all font-medium"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* 📚 BOOKS TABLE */}
      <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50">
                <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Book Details</th>
                <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Category</th>
                <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Author</th>
                <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Created</th>
                <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredBooks.map((book) => (
                <tr key={book.id} className="hover:bg-slate-50/50 transition-colors group">
                  <td className="px-8 py-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-16 bg-slate-100 rounded-lg overflow-hidden shrink-0">
                        {book.thumbnail && <img src={book.thumbnail} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div>
                        <p className="font-black text-slate-900 group-hover:text-indigo-600 transition-colors">{book.title}</p>
                        <p className="text-xs font-medium text-slate-400 mt-1 line-clamp-1">{book.description}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-6">
                    <span className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-[10px] font-black uppercase tracking-widest border border-indigo-100">
                      {book.category}
                    </span>
                  </td>
                  <td className="px-8 py-6 font-bold text-slate-600">{book.author}</td>
                  <td className="px-8 py-6 text-sm text-slate-400">{new Date(book.created_at).toLocaleDateString()}</td>
                  <td className="px-8 py-6">
                    <div className="flex items-center gap-3">
                      <a href={book.pdf_url} target="_blank" rel="noreferrer" className="p-2 text-slate-400 hover:text-indigo-600 transition-colors">
                        <ExternalLink size={18} />
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredBooks.length === 0 && !loading && (
            <div className="p-20 text-center text-slate-400 font-bold uppercase tracking-widest italic">
              No books found
            </div>
          )}
        </div>
      </div>

      {/* ➕ ADD BOOK MODAL */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white w-full max-w-2xl rounded-[2.5rem] shadow-2xl overflow-hidden"
            >
              <div className="p-8 border-b border-slate-50 flex items-center justify-between bg-slate-50/50">
                <div>
                  <h2 className="text-2xl font-black text-slate-900">Add New Book</h2>
                  <p className="text-sm font-medium text-slate-500">Enter details to publish a new book</p>
                </div>
                <button onClick={() => setShowModal(false)} className="p-2 bg-white rounded-xl shadow-sm text-slate-400 hover:text-slate-900">
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-8 space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Book Title</label>
                    <input 
                      required
                      type="text" 
                      className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600/10 font-bold"
                      placeholder="e.g. Modern Banking"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Author Name</label>
                    <input 
                      required
                      type="text" 
                      className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600/10 font-bold"
                      placeholder="e.g. Dr. John Doe"
                      value={formData.author}
                      onChange={(e) => setFormData({...formData, author: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Category</label>
                    <select 
                      className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600/10 font-bold appearance-none"
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                    >
                      <option value="General">General</option>
                      <option value="Banking">Banking</option>
                      <option value="SSC">SSC</option>
                      <option value="Railway">Railway</option>
                      <option value="UPSC">UPSC</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Description</label>
                    <input 
                      type="text" 
                      className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600/10 font-bold"
                      placeholder="Brief overview..."
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Cover Image</label>
                    <div className="relative group">
                      <input 
                        type="file" 
                        accept="image/*"
                        className="hidden" 
                        id="thumb-upload"
                        onChange={(e) => { handleFileUpload(e, 'thumbnail'); e.target.value = null; }}
                      />
                      <label 
                        htmlFor="thumb-upload"
                        className="flex flex-col items-center justify-center gap-2 p-6 bg-slate-50 border-2 border-dashed border-slate-200 rounded-[2rem] cursor-pointer group-hover:border-indigo-600 transition-all"
                      >
                        <UploadCloud className="text-slate-300 group-hover:text-indigo-600" size={32} />
                        <span className="text-[10px] font-black text-slate-400 uppercase group-hover:text-indigo-600">
                          {formData.thumbnail ? 'Image Selected' : 'Upload Thumbnail'}
                        </span>
                      </label>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">PDF File</label>
                    <div className="relative group">
                      <input 
                        type="file" 
                        accept=".pdf"
                        className="hidden" 
                        id="pdf-upload"
                        onChange={(e) => { handleFileUpload(e, 'pdf_url'); e.target.value = null; }}
                      />
                      <label 
                        htmlFor="pdf-upload"
                        className="flex flex-col items-center justify-center gap-2 p-6 bg-slate-50 border-2 border-dashed border-slate-200 rounded-[2rem] cursor-pointer group-hover:border-indigo-600 transition-all"
                      >
                        <FileText className="text-slate-300 group-hover:text-indigo-600" size={32} />
                        <span className="text-[10px] font-black text-slate-400 uppercase group-hover:text-indigo-600">
                          {formData.pdf_url ? 'PDF Selected' : 'Upload PDF'}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <button 
                  disabled={uploading}
                  type="submit"
                  className="w-full py-5 bg-indigo-600 text-white font-black rounded-2xl shadow-2xl shadow-indigo-200 hover:bg-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {uploading ? 'Uploading...' : 'Publish Book'}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
